/** 展示层的小工具：时间、id、枚举中文名。 */

import type { OrgRole, OrgType } from './types.ts';

const dateTimeFormat = new Intl.DateTimeFormat('zh-CN', {
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
  hour12: false,
});

const dateFormat = new Intl.DateTimeFormat('zh-CN', {
  year: 'numeric', month: '2-digit', day: '2-digit',
});

export function formatDateTime(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return dateTimeFormat.format(date);
}

export function formatDate(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return dateFormat.format(date);
}

/** 相对时间，用于「最后使用」这类字段。 */
export function formatRelative(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '从未';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const deltaSec = Math.round((Date.now() - date.getTime()) / 1000);
  if (deltaSec < 60) return '刚刚';
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)} 分钟前`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)} 小时前`;
  if (deltaSec < 2592000) return `${Math.floor(deltaSec / 86400)} 天前`;
  return formatDate(iso);
}

/**
 * id 缩写：`org_01M06Q1XPZ3D19F3KTP32VPYBR` → `org_…2VPYBR`。
 * 表格里 id 一多就把内容挤没了，但**尾部必须留够**：ULID 是时间有序的，
 * 同一秒内创建的几条只在尾部有区别，砍太短会让两行看起来一模一样。
 */
export function shortId(id: string, tailLength = 6): string {
  const underscore = id.indexOf('_');
  if (underscore < 0 || id.length <= underscore + 1 + tailLength + 2) return id;
  return `${id.slice(0, underscore + 1)}…${id.slice(-tailLength)}`;
}

export const ORG_TYPE_LABEL: Readonly<Record<OrgType, string>> = {
  platform: '平台',
  agent: '代理',
  merchant: '商户',
};

export const ROLE_LABEL: Readonly<Record<OrgRole, string>> = {
  owner: '所有者',
  admin: '管理员',
  operator: '操作员',
  viewer: '只读',
};

/** 角色权级，与后端 `roleAtLeast` 一致。 */
const ROLE_RANK: Readonly<Record<OrgRole, number>> = {
  viewer: 0, operator: 1, admin: 2, owner: 3,
};

export function roleAtLeast(role: OrgRole, minimum: OrgRole): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}

export const STATUS_LABEL: Readonly<Record<string, string>> = {
  // 组织 / 通道 / 绑定
  active: '启用', suspended: '暂停', closed: '已关闭',
  paused: '暂停', disabled: '停用',
  // 支付
  created: '已创建', processing: '处理中', requires_action: '待验证',
  authorized: '已授权', captured: '已请款', partially_refunded: '部分退款',
  refunded: '已退款', failed: '失败', cancelled: '已取消', expired: '已过期',
  // 退款
  pending: '待处理', succeeded: '成功',
  // 争议
  open: '待处理', under_review: '审核中', won: '胜诉', lost: '败诉', accepted: '已接受',
  // 提现
  draft: '草稿', pending_approval: '待审批', approved: '已批准', paid: '已出款',
  rejected: '已驳回',
  // 结算批次（`closed` 与组织的「已关闭」撞名，走下面的 DOMAIN_OVERRIDE）
  settling: '结算中', settled: '已结清',
  // 凭证
  expiring: '将过期', revoked: '已吊销',
  // 收款链接
  void: '已作废',
};

/**
 * 同一个状态值在不同实体上语义相反时的按实体覆盖。
 *
 * 状态值是各张表自己定的，撞名不可避免，而 `STATUS_LABEL` / `statusTone` 是一张
 * 全局表 —— 撞上了就只能有一方是错的。`closed` 就是活的例子：
 *
 *   - `organizations.status='closed'`      已关闭，不可逆（orgs 页那里是 danger 按钮）→ 红是对的
 *   - `settlement_batches.status='closed'` 已封账，正常流程的中间态          → 红是错的
 *
 * 生产上 9 条批次里 7 条是 closed，于是整页结算批次显示成红色的「已关闭」——
 * 标签和颜色两个都错。曾经有人加过一个 `closed_batch` 键想绕开，但没有任何东西
 * 会去查它（状态值是 `closed`，不是 `closed_batch`），所以那条一直是死的。
 *
 * 与其让各页面自己维护一份映射（那就回到了 StatusBadge 注释想避免的老路），
 * 不如让撞名这件事在这里显式登记一次：只有真撞名的才进来，其余照走全局表。
 */
const DOMAIN_OVERRIDE: Readonly<Record<string, Readonly<Record<string, { label: string; tone: Tone }>>>> = {
  settlement_batch: {
    // 累计中 = 这批还在收结算腿。全局表里 `open` 是争议的「待处理」（warn，
    // 意思是「等着你处理」），而这里没有任何人需要做什么，是正常累计。
    open: { label: '累计中', tone: 'info' },
    // 已封账 = 这批不再接新的结算腿了，是流程正常推进的一步，不是失败。
    closed: { label: '已封账', tone: 'good' },
  },
};

/** 状态文案。`domain` 只在状态值跨实体撞名时才需要传，见 {@link DOMAIN_OVERRIDE}。 */
export function statusLabel(status: string, domain?: string): string {
  const override = domain === undefined ? undefined : DOMAIN_OVERRIDE[domain]?.[status];
  if (override !== undefined) return override.label;
  return STATUS_LABEL[status] ?? status;
}

/**
 * 状态的语义色分档。**与主色（accent）完全分开** ——
 * 主色表示「可点」，这里表示「事情本身好不好」，混用会让人分不清哪个是按钮。
 */
export type Tone = 'good' | 'warn' | 'bad' | 'neutral' | 'info';

/** 状态色调。`domain` 同 {@link statusLabel}：只在跨实体撞名时才需要传。 */
export function statusTone(status: string, domain?: string): Tone {
  const override = domain === undefined ? undefined : DOMAIN_OVERRIDE[domain]?.[status];
  if (override !== undefined) return override.tone;
  switch (status) {
    case 'active': case 'captured': case 'succeeded': case 'paid':
    case 'settled': case 'won': case 'approved': case 'authorized':
      return 'good';
    case 'failed': case 'lost': case 'disabled': case 'closed':
    case 'expired': case 'rejected': case 'revoked':
      return 'bad';
    case 'void':
      return 'neutral';
    case 'paused': case 'suspended': case 'pending_approval': case 'requires_action':
    case 'expiring': case 'open': case 'under_review': case 'partially_refunded':
      return 'warn';
    case 'processing': case 'settling': case 'pending': case 'created': case 'draft':
      return 'info';
    default:
      return 'neutral';
  }
}

/** 从物化路径拆出祖先 id 列表（不含尾部空段）。 */
export function pathSegments(path: string): readonly string[] {
  return path.split('/').filter((segment) => segment !== '');
}

/** 组织树深度：平台 1，代理 2，商户 3。 */
export function pathDepth(path: string): number {
  return pathSegments(path).length;
}
